import { Feature } from '@tencent/workbench';
import { BorderFeature } from '@framework/workbench/drawer/ui/border/border-feature';
import { IChartModel } from '@src/slide/domain/ui-model/element-modeles/Interface/chart-v3/chart-model.interface';
import { UIModelType } from '@src/slide/domain/ui-model/element-modeles/constant';
import { BorderType } from '@src/slide/domain/ui-model/element-modeles/property/border/border.interface';
import { getPcInjectedService } from '@src/framework/workbench/util/injector-service';
import { ChartService } from '@src/framework/workbench/common/service/chart-service';
import { IChartLineFeature } from './line-interface';
import { ColorSelectButton } from '@framework/workbench/drawer/components/color-select-button';
import React from 'react';
import { AngleFrom, NumberInputFrom } from '@tencent/wb-ext-form';
import { FormItemName, INPUT_PROPS_CALLBACK_KEY } from '@src/framework/workbench/drawer/constants';
import { handleInputBlurInitAndReset, IInputParams } from '@src/slide/application/service/drawer-input-manager/drawer-handler';
import { canvasToPt } from '@src/slide/infrastructure/module/independent/unit';
import { alphaToOpacity, opacityToAlpha } from '@src/framework/workbench/drawer/utils';
import i18n from '@i18next/i18n';
import { BorderLine, DrawerBorderType } from '@tencent/drawing-drawer-components';

const borderTypeMap = {
    [BorderType.GradualFill]: DrawerBorderType.GRAD_FILL,
    [BorderType.SolidFill]: DrawerBorderType.SOLID_FILL,
    [BorderType.NoFill]: DrawerBorderType.NO_FILL,
};

const DEFAULT_STYLE = 'solid';

@Feature()
export class ChartLineFeature extends BorderFeature implements IChartLineFeature {
    protected defaultColor = '#D9D9D9';
    protected defalutOpacity = 0;
    protected sdkGradualAngle;
    protected sdkGradualOpacity;

    /**
     * 获取当前选中元素
     */
    public getSelectModel(): any {
        const model = super.getSelectModel();
        if (model?.type === UIModelType.CHART) {
            const chartService = (getPcInjectedService(ChartService) as ChartService);
            const selectedElementModel = chartService.getSelectedElementModel(model);
            return selectedElementModel;
        }
        return model;
    }

    public get borderInfo() {
        const selectedElement = this.getSelectModel();
        const info = selectedElement?.getBorder?.()?.getBorderInfo() || {};
        return {
            ...info,
            style: info.style || DEFAULT_STYLE,
        };
    }

    public get borderType() {
        const selectedElement = this.getSelectModel();
        const borderType = selectedElement?.getBorder?.()?.getBorderType();
        if (this.isMultiDifferent() || !borderType) {
            return DrawerBorderType.NO_SELECT;
        }
        return borderTypeMap[borderType] || DrawerBorderType.NO_FILL;
    }

    @handleInputBlurInitAndReset()
    public handleBorderWidthChange(value, from?: NumberInputFrom) {
        const isOnlyChangeUI = this.isInputOnlyChangeUI();
        if (!isOnlyChangeUI && canvasToPt(this.borderInfo?.width) === value) return;
        this.width = value;
        this.updatePropsByType(isOnlyChangeUI, true);
    }

    @handleInputBlurInitAndReset()
    public handleOpacityChange(opacity: number, dragEnd?: boolean, from?: NumberInputFrom) {
        const isOnlyChangeUI = this.isInputOnlyChangeUIWithDrag(dragEnd);
        if (from === NumberInputFrom.INPUT) {
            if (isOnlyChangeUI) return;
            // 避免数据重复提交。 case： 回车后再失焦，会触发两次change回调
            if (this.opacity === opacity) return;
            super.handleOpacityChange(opacity, dragEnd, from);
        } else {
            super.handleOpacityChange(opacity, dragEnd, from);
        }
    }

    @handleInputBlurInitAndReset()
    public handleGradualAngleChange(value: number, from?: AngleFrom, dragEnd?: boolean): void {
        const isOnlyChangeUI = this.isInputOnlyChangeUIWithDrag(dragEnd);
        if (from === NumberInputFrom.INPUT) {
            if (isOnlyChangeUI) return;
            // 避免数据重复提交。 case： 回车后再失焦，会触发两次change回调
            if (this.borderGradualValues.curAngle === value) return;
            super.handleGradualAngleChange(value, from, dragEnd);
        } else {
            super.handleGradualAngleChange(value, from, dragEnd);
        }
    }

    @handleInputBlurInitAndReset()
    public handleGradualOpacityChange(opacity: number, from?: NumberInputFrom, dragEnd?: boolean): void {
        const isOnlyChangeUI = this.isInputOnlyChangeUIWithDrag(dragEnd);
        if (from === NumberInputFrom.INPUT) {
            if (isOnlyChangeUI) return;
            // 避免数据重复提交。 case： 回车后再失焦，会触发两次change回调
            if (this.borderGradualValues.activeColor?.alpha === opacityToAlpha(opacity)) return;
            super.handleGradualOpacityChange(opacity, from, dragEnd);
        } else {
            super.handleGradualOpacityChange(opacity, from, dragEnd);
        }
    }

    public getColorSelectButton() {
        return <ColorSelectButton
            onColorChange={this.changeBorderColor}
            value={this.borderColor}
        />;
    }

    /**
     * 获取输入框装饰符所需的参数
     */
    public getInputParams(): IInputParams {
        return {
            key: INPUT_PROPS_CALLBACK_KEY.CHART_BORDER,
        };
    }

    public renderBorderLine() {
        this.updateUIWithBorderData();
        return (<BorderLine
            key={FormItemName.BORDER_LINE}
            style={this.style}
            handleBorderStyleClick={value => this.handleBorderStyleClick(value, FormItemName.BORDER_LINE)}
            handleBorderMenuMouseEnter={value => this.handleBorderMenuMouseEnter(value, FormItemName.BORDER_LINE)}
            handleBorderMenuMouseLeave={this.handleBorderMenuMouseLeave}
            tooltip={i18n.t('线条类型')}
        />);
    }

    protected updateBorder(border, onlyChangeUI?) {
        // 目前图表不支持 预览样式，目前采取脱手更新并保存
        if (onlyChangeUI) return;
        const selectedElement = this.getSelectModel();
        return selectedElement?.updateBorder?.(border);
    }

    protected updateNoFillProps() {
        const border = { color: null, gradFill: null, width: 0, noFill: 1 };
        this.updateBorder(border);
    }
}
